/** 
 * Check if two arrays or sets contain the same elements, regardless of order.
 * @param {Array|Set} listA - First array or set to compare.
 * @param {Array|Set} listB - Second array or set to compare.
 * @param {Function} [getKeyA] - Optional function to extract a key/id from elements of listA.
 * @param {Function} [getKeyB] - Optional function to extract a key/id from elements of listB.
 * @returns {boolean} - True if both lists contain the same elements, false otherwise.
 * @example
 * isEquivalent([1, 2, 3], [3, 2, 1]); // returns true
 * isEquivalent(new Set(['a', 'b']), new Set(['b', 'a'])); // returns true
 * isEquivalent([{id: 1}, {id: 2}], [2, 1], x => x.id); // returns true
 */
export function isEquivalent(listA, listB, getKeyA, getKeyB) {
  const setA = new Set(getKeyA ? listA.map(getKeyA) : listA);
  const setB = new Set(getKeyB ? listB.map(getKeyB) : listB);

  if (setA.size !== setB.size) {
    return false;
  }

  return !setA.symmetricDifference(setB).size;
}

/**
 * Get the difference between two arrays or sets, regardless of order.
 * @param {Array|Set} listA - The array or set to subtract from.
 * @param {Array|Set} listB - The array or set to subtract.
 * @param {Function} [getKeyA] - Optional function to extract a key/id from elements of listA.
 * @param {Function} [getKeyB] - Optional function to extract a key/id from elements of listB.
 * @returns {Set} - A set containing elements in listA that are not in listB.
 * @example
 * difference([1, 2, 3, 4], [2, 4]); // returns Set {1, 3}
 * difference(new Set(['a', 'b', 'c']), new Set(['b'])); // returns Set {'a', 'c'}
 * difference([{id: 1}, {id: 2}, {id: 3}], [2, 3], x => x.id); // returns Set {1}
 */

export function difference(listA, listB, getKeyA, getKeyB) {
  const setA = new Set(getKeyA ? listA.map(getKeyA) : listA);
  const setB = new Set(getKeyB ? listB.map(getKeyB) : listB);
  
  return setA.difference(setB);
}

/**
 * Check if one array or set contains all elements of another, regardless of order.
 * @param {Array|Set} listA - The array or set to check as superset.
 * @param {Array|Set} listB - The array or set to check as subset.
 * @param {Function} [getKeyA] - Optional function to extract a key/id from elements of listA.
 * @param {Function} [getKeyB] - Optional function to extract a key/id from elements of listB.
 * @returns {boolean} - True if listA contains all elements of listB, false otherwise.
 * @example
 * isSuperset([1, 2, 3, 4], [2, 3]); // returns true
 * isSuperset(new Set(['a', 'b', 'c']), ['a', 'd']); // returns false
 * isSuperset(["hello", "world"], ["WORLD"], str => str.toLowerCase(), str => str.toLowerCase()); // returns true
 */
export function isSuperset(listA, listB, getKeyA, getKeyB) {
  const setA = new Set(getKeyA ? listA.map(getKeyA) : listA);
  const setB = new Set(getKeyB ? listB.map(getKeyB) : listB);

  return setA.isSupersetOf(setB);
}

/**
 * Check if an array or set contains all specified values.
 * @param {Array|Set} collection - The array or set to check.
 * @param {...*} values - Values to look for in the collection.
 * @returns {boolean} - True if the collection contains all specified values, false otherwise.
 * @example
 * contains([1, 2, 3, 4], 2, 3); // returns true
 * contains(new Set(['a', 'b', 'c']), 'a', 'd'); // returns false
 */
export function contains(collection, ...values) {
  return isSuperset(collection, values);
}